import { HttpClient, HttpParams } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { appendHttpParamsFromObject, convertISOToDateOnly, TreetopPropertiesProvider } from 'shared/util-common';

import { GuidelineStats, GuidelineViews, PublicViewLocationCountry, TrendingStats, UserStats } from '@treetop/shared/model';

export enum ChartTrendsRequestType {
  LoginTrends = 'login-trends',
  GuidelineViewTrends = 'guideline-view-trends',
  GuidelineEditTrends = 'guideline-edit-trends',
}

export interface StatisticsRequestOptions {
  includeOrganisationTree?: boolean;
}

export interface PublicStatisticsRequestOptions extends StatisticsRequestOptions {
  countries?: string[];
  regions?: string[];
}

@Injectable({ providedIn: 'root' })
export class StatisticsHttpService {
  private http = inject(HttpClient);
  private readonly baseUrl = `${TreetopPropertiesProvider.properties.baseUrl}api/statistics`;

  public getGuidelineStats(options?: StatisticsRequestOptions): Observable<GuidelineStats> {
    const params = options?.includeOrganisationTree ? new HttpParams().set('includeOrganisationTree', 'true') : undefined;
    return this.http.get<GuidelineStats>(`${this.baseUrl}/total-guidelines`, { params });
  }

  public getUserStats(options?: StatisticsRequestOptions): Observable<UserStats> {
    const params = options?.includeOrganisationTree ? new HttpParams().set('includeOrganisationTree', 'true') : undefined;
    return this.http.get<UserStats>(`${this.baseUrl}/total-users`, { params });
  }

  public getTopViewedGuidelines(
    fromDate: Date,
    toDate: Date,
    searchTerm?: string,
    options?: StatisticsRequestOptions,
  ): Observable<GuidelineViews[]> {
    let params = new HttpParams();
    const convertedParams = {
      fromDate: convertISOToDateOnly(fromDate),
      toDate: convertISOToDateOnly(toDate),
    };

    params = appendHttpParamsFromObject(convertedParams, params);

    if (searchTerm) {
      params = params.set('searchTerm', searchTerm);
    }

    if (options?.includeOrganisationTree) {
      params = params.set('includeOrganisationTree', 'true');
    }

    return this.http.get<GuidelineViews[]>(`${this.baseUrl}/top-viewed-guidelines`, { params });
  }

  public getTopViewedPublicGuidelines(
    fromDate: Date,
    toDate: Date,
    searchTerm?: string,
    options?: PublicStatisticsRequestOptions,
  ): Observable<GuidelineViews[]> {
    let params = new HttpParams();

    const convertedParams = {
      fromDate: convertISOToDateOnly(fromDate),
      toDate: convertISOToDateOnly(toDate),
    };

    params = appendHttpParamsFromObject(convertedParams, params);

    if (searchTerm) {
      params = params.set('searchTerm', searchTerm);
    }

    if (options?.includeOrganisationTree) {
      params = params.set('includeOrganisationTree', 'true');
    }

    params = this.appendCountryRegionParams(params, options);

    return this.http.get<GuidelineViews[]>(`${this.baseUrl}/top-viewed-public-guidelines`, { params });
  }

  public getLoginTrendsAsync(
    fromDate: Date,
    toDate: Date,
    compareToFromDate: Date,
    compareToToDate: Date,
    options?: StatisticsRequestOptions,
  ): Observable<TrendingStats> {
    let params = new HttpParams();

    const convertedParams = {
      fromDate: convertISOToDateOnly(fromDate),
      toDate: convertISOToDateOnly(toDate),
      compareToFromDate: convertISOToDateOnly(compareToFromDate),
      compareToToDate: convertISOToDateOnly(compareToToDate),
    };

    params = appendHttpParamsFromObject(convertedParams, params);

    if (options?.includeOrganisationTree) {
      params = params.set('includeOrganisationTree', 'true');
    }

    return this.http.get<TrendingStats>(`${this.baseUrl}/login-trends`, { params });
  }

  public getGuidelineViewTrendsAsync(
    fromDate: Date,
    toDate: Date,
    compareToFromDate: Date,
    compareToToDate: Date,
    options?: StatisticsRequestOptions,
  ): Observable<TrendingStats> {
    let params = new HttpParams();

    const convertedParams = {
      fromDate: convertISOToDateOnly(fromDate),
      toDate: convertISOToDateOnly(toDate),
      compareToFromDate: convertISOToDateOnly(compareToFromDate),
      compareToToDate: convertISOToDateOnly(compareToToDate),
    };

    params = appendHttpParamsFromObject(convertedParams, params);

    if (options?.includeOrganisationTree) {
      params = params.set('includeOrganisationTree', 'true');
    }

    return this.http.get<TrendingStats>(`${this.baseUrl}/guideline-view-trends`, { params });
  }

  public getPublicGuidelineViewTrendsAsync(
    fromDate: Date,
    toDate: Date,
    compareToFromDate: Date,
    compareToToDate: Date,
    options?: PublicStatisticsRequestOptions,
  ): Observable<TrendingStats> {
    let params = new HttpParams();

    const convertedParams = {
      fromDate: convertISOToDateOnly(fromDate),
      toDate: convertISOToDateOnly(toDate),
      compareToFromDate: convertISOToDateOnly(compareToFromDate),
      compareToToDate: convertISOToDateOnly(compareToToDate),
    };

    params = appendHttpParamsFromObject(convertedParams, params);

    if (options?.includeOrganisationTree) {
      params = params.set('includeOrganisationTree', 'true');
    }

    params = this.appendCountryRegionParams(params, options);

    return this.http.get<TrendingStats>(`${this.baseUrl}/public-guideline-view-trends`, { params });
  }

  public getGuidelineEditTrendsAsync(
    fromDate: Date,
    toDate: Date,
    compareToFromDate: Date,
    compareToToDate: Date,
    options?: StatisticsRequestOptions,
  ): Observable<TrendingStats> {
    let params = new HttpParams();

    const convertedParams = {
      fromDate: convertISOToDateOnly(fromDate),
      toDate: convertISOToDateOnly(toDate),
      compareToFromDate: convertISOToDateOnly(compareToFromDate),
      compareToToDate: convertISOToDateOnly(compareToToDate),
    };

    params = appendHttpParamsFromObject(convertedParams, params);

    if (options?.includeOrganisationTree) {
      params = params.set('includeOrganisationTree', 'true');
    }

    return this.http.get<TrendingStats>(`${this.baseUrl}/guideline-edit-trends`, { params });
  }

  public getPublicViewLocations(fromDate: Date, toDate: Date, options?: StatisticsRequestOptions): Observable<PublicViewLocationCountry[]> {
    let params = new HttpParams();

    const convertedParams = {
      fromDate: convertISOToDateOnly(fromDate),
      toDate: convertISOToDateOnly(toDate),
    };

    params = appendHttpParamsFromObject(convertedParams, params);

    if (options?.includeOrganisationTree) {
      params = params.set('includeOrganisationTree', 'true');
    }

    return this.http.get<PublicViewLocationCountry[]>(`${this.baseUrl}/public-view-locations`, { params });
  }

  public getAttachmentStatistics(
    attachmentId: string,
    uniqueId: string,
    fromDate: Date,
    toDate: Date,
    compareToFromDate: Date,
    compareToToDate: Date,
  ): Observable<TrendingStats> {
    let params = new HttpParams();

    const convertedParams = {
      fromDate: convertISOToDateOnly(fromDate),
      toDate: convertISOToDateOnly(toDate),
      compareToFromDate: convertISOToDateOnly(compareToFromDate),
      compareToToDate: convertISOToDateOnly(compareToToDate),
      attachmentId,
      uniqueId: uniqueId ?? '',
    };

    params = appendHttpParamsFromObject(convertedParams, params);

    return this.http.get<TrendingStats>(`${this.baseUrl}/attachment-download-trends`, { params });
  }

  private appendCountryRegionParams(params: HttpParams, options?: PublicStatisticsRequestOptions): HttpParams {
    options?.countries?.forEach((country) => {
      params = params.append('countries', country);
    });

    options?.regions?.forEach((region) => {
      params = params.append('regions', region);
    });

    return params;
  }
}
